import {
  DeleteOutlined,
  MailOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Form,
  Image,
  Input,
  Layout,
  Spin,
  Tabs,
  Typography,
  Upload,
  message,
} from 'antd';
import { setCommonRefreshAction } from 'common/commonRedux/auth/actions';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { useAxiosRequest } from 'common/hooks';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ChangePassword from './ChangePassword/ChangePassword';
import './index.css';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from 'common/config/baseUrl';
const { Content } = Layout;
const { TabPane } = Tabs;

const ProfileSettings = () => {
  const { t } = useTranslation()
  const { profileData, originalTokenData } = useSelector((state: any) => state.auth, shallowEqual);
  // Settings always edits the real logged-in user's profile, never the
  // impersonated account.
  const realAccountId = originalTokenData?.accountid ?? profileData?.tokenData?.accountid;
  const {
    data: organizationData = {},
    refetch,
    isLoading,
    isFetching,
  } = useGetDataQuery(
    {
      urlObjKey: 'GetOrganizationProfile',
      params: { accountId: realAccountId },
      useOriginalToken: true,
    },
    { skip: !realAccountId, refetchOnMountOrArgChange: false },
  );
  const [form] = Form.useForm();
  const uploadUrl = `${BASE_URL}/api/Document/Upload?accountId=${realAccountId}&fileType=image`;
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dispatch: any = useDispatch();
  const updateProfile = useAxiosRequest('');
  // Handle file preview and selection
  const handlePreview = async (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setPreviewImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
    setSelectedFile(file);
    return false; // Prevent automatic upload
  };

  // Handle file upload
  const handleUpload = async (file: File) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        return data.fileUrlId;
      } else {
        message.error('Image upload failed.');
        return null;
      }
    } catch (error) {
      message.error('Error uploading image.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Handle file delete
  const handleDelete = () => {
    setPreviewImage(null);
    setSelectedFile(null);
  };
  const onFinish = async (values: any) => {
    if (!selectedFile) {
      message.error('Please upload a profile image.');
      return;
    }
    const uploadedImgId = await handleUpload(selectedFile);
    if (!uploadedImgId) return;
    const data = {
      accountId: realAccountId,
      organizationName: values?.orgName,
      adminName: values?.adminName,
      imageId: uploadedImgId,
    };
    updateProfile.apiAction({
      urlObjKey: 'UpdateOrganizationProfile',
      payload: data,
      method: 'POST',
      isToast: true,
      useOriginalToken: true,
      cb: () => {
        refetch();
        setSelectedFile(null);
        setPreviewImage(null);
      },
      onError: () => { },
    });
  };
  const handleRefresh = () => {
    refetch();
  };
  useEffect(() => {
    dispatch(
      setCommonRefreshAction({
        CB: () => {
          handleRefresh();
        },
      })
    );
    return () => {
      dispatch(setCommonRefreshAction({}));
    };
  }, []);
  return (
    <Card style={{margin: "0px !important", padding: "0px"}} className="settings-insider !p-0 !m-0">
      {isLoading || isFetching ? (
        <div className="min-h-[60vh] grid place-content-center">
          <Spin />
        </div>
      ) : (
        <Content style={{ padding: 0, margin: 0 }}>
          <div className="mb-4 overflow-hidden !border p-5 !rounded-xl">
            <div className="flex items-center">
              {organizationData?.profileImgUrl ? (
                <img
                  className="size-[96px] rounded-full mr-5"
                  src={organizationData?.profileImgUrl}
                  alt=""
                />
              ) : (
                <Avatar
                  size={96}
                  src={''}
                  icon={<UserOutlined />}
                  className="mr-4"
                />
              )}
              <div>
                <Typography.Text className="text-sm font-medium ">
                  {t('settings:organization')}
                </Typography.Text>
                <Typography.Title level={2} style={{ margin: 0 }} className="mb-1 !text-xl !font-bold">
                  {organizationData?.organizationName}
                </Typography.Title>
                <Typography.Text>{t('settings:email')}: {organizationData?.email}</Typography.Text>
              </div>
            </div>
          </div>
          <div className=' !border p-5 !rounded-xl'>
            <Tabs defaultActiveKey="1">
              <TabPane tab={t('settings:tabs:updateInfo')} key="1">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  initialValues={{
                    orgName: organizationData?.organizationName,
                    email: organizationData?.email,
                    adminName: organizationData?.adminName,
                  }}
                >
                  <div className="flex flex-col md:flex-row md:space-x-4 w-full">
                    <div className="flex-1">
                      <Form.Item
                        name="orgName"
                        label={
                          <Typography.Title style={{ margin: 0 }} className=" !text-md !font-semibold">
                            {t('settings:updateInfo:orgName')}
                          </Typography.Title>
                        }
                      >
                        <Input
                          prefix={
                            <UserOutlined className="site-form-item-icon mr-1 text-gray-500" />
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label={

                          <Typography.Title style={{ margin: 0 }} className=" !text-md !font-semibold">
                            {t('settings:updateInfo:orgEmail')}
                          </Typography.Title>
                        }
                      >
                        <Input
                          prefix={
                            <MailOutlined className="site-form-item-icon mr-1 text-gray-500" />
                          }
                          disabled
                        />
                      </Form.Item>
                      <Form.Item
                        name="adminName"
                        label={

                          <Typography.Title style={{ margin: 0 }} className=" !text-md !font-semibold">
                            {t('settings:updateInfo:adminName')}
                          </Typography.Title>
                        }
                      >
                        <Input
                          prefix={
                            <UserOutlined className="site-form-item-icon mr-1 text-gray-500" />
                          }
                        />
                      </Form.Item>
                    </div>
                    {/* Image Upload Section */}
                    <div className="border-l-2 border-none md:border-dashed border-gray-200 pl-0 md:pl-4 flex-1">
                      <div className="mb-4">
                        <Typography.Title style={{ marginBottom: 8 }} className=" !text-md !font-bold">
                          {t('settings:updateInfo:profilePhoto')}
                          <span style={{ color: 'red', marginLeft: 3 }}>
                            *
                          </span>
                        </Typography.Title>
                        <div className="flex flex-col items-start gap-4">
                          {!previewImage ? (
                            <Upload
                              name="file"
                              maxCount={1}
                              accept="image/png, image/jpeg, image/webp"
                              beforeUpload={handlePreview}
                              listType="picture-card"
                              showUploadList={false}
                            >
                              <div>
                                <PlusOutlined />{t('settings:updateInfo:upload')}
                              </div>
                            </Upload>
                          ) : (
                            <div className="relative">
                              <Image
                                className="!w-[150px] !h-[120px] object-fill !rounded-md border hover:border-primaryColor border-dashed"
                                preview={{
                                  visible: previewOpen,
                                  onVisibleChange: (visible) =>
                                    setPreviewOpen(visible),
                                }}
                                src={
                                  previewImage ||
                                  organizationData?.profileImgUrl
                                }
                                onClick={() => setPreviewOpen(true)}
                              />
                              <DeleteOutlined
                                onClick={handleDelete}
                                className="absolute -top-2 -right-2 text-red-500 bg-white p-1 rounded-full cursor-pointer"
                                style={{ fontSize: '18px' }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="px-4 mt-6 py-2 bg-primaryColor/10 border-l-4 border-primaryColor/80 rounded-md">
                        <p className="text-sm font-medium">
                          <span className="font-semibold">{t('settings:updateInfo:nb')}</span>{' '}
                          {t('settings:updateInfo:note')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='flex h-full items-end w-full justify-end mt-4 !mb-0 !pb-0'>
                    <Form.Item className='!pb-0 !mb-0'>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size='large'
                        loading={updateProfile.isLoading || uploading}
                      >
                        {updateProfile.isLoading || uploading
                          ? t('settings:updateInfo:updating')
                          : t('settings:updateInfo:updateProfile')}
                      </Button>
                    </Form.Item>
                  </div>
                </Form>
              </TabPane>
              <TabPane tab={t('settings:tabs:changePassword')} key="2">
                <ChangePassword />
              </TabPane>
            </Tabs>
          </div>
        </Content>
      )}
    </Card>
  );
};

export default ProfileSettings;
